<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import {
  ToccoByIdQueryDocument,
  ToccoCourseFieldsFragment,
  ToccoELearningFieldsFragment,
  ToccoIntroModuleFieldsFragment,
  ToccoWebinarFieldsFragment,
} from '~/api/graphql/generated'
import { FormKitMessages } from '@formkit/vue'
import { createMetaInfoObject } from '~/lib'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

useSeoMeta(
  createMetaInfoObject([
    ['title', `${t('seo.pageTitles.moduleRegistration')} | Careum`],
    ['robots', 'noindex, nofollow'],
  ]),
)

const route = useRoute()

const { data } = await useAsyncQuery(ToccoByIdQueryDocument, { id: route.params.id })
const computedData = computed(() => data.value?.toccoById) as ComputedRef<
  | ToccoCourseFieldsFragment
  | ToccoELearningFieldsFragment
  | ToccoIntroModuleFieldsFragment
  | ToccoWebinarFieldsFragment
  | undefined
>

const formData = ref()
const showReview = ref(false)

const showFphMemberNumber = ref(computedData.value?.__typename == 'ToccoWebinar' ? false : true)
const showCompanyAddress = ref(true)
const showInvoiceAddress = ref(false)

const addressRequired = ref(true)
if (computedData.value?.__typename == 'ToccoWebinar') {
  watch(formData, () => {
    addressRequired.value =
      !showCompanyAddress.value || formData?.value?.invoiceAddress?.invoiceAddressActive == 'privateAddress'
  })
}

const hideCompany = computed({
  get: () => !showCompanyAddress.value,
  set: (checked: boolean) => {
    if (formData.value?.invoiceAddress?.invoiceAddressActive) {
      formData.value.invoiceAddress.invoiceAddressActive = 'privateAddress'
    }
    showCompanyAddress.value = !checked
  },
})

const handleSubmit = async () => {
  showReview.value = true
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const onCancel = () => {
  showReview.value = false
  window.scrollTo({ top: 0, behavior: 'smooth' })
}
</script>

<template>
  <Section class="grid register-form-layout">
    
<FormKitLazyProvider config-file="true">
<div class="ds-8 ds-o-1">
      <FormKit
        v-show="!showReview"
        v-model="formData"
        type="form"
        :actions="false"
        :incomplete-message="t('tocco.registration.module.errors')"
        @submit="handleSubmit()"
      >
        <div class="form-title">
          <h6>{{ t('tocco.registration.module.headings.subtitle') }}</h6>
          <h1>{{ t('tocco.registration.module.headings.title') }}</h1>
          <p>
            {{ t('tocco.registration.module.headings.description') }}
          </p>
        </div>

        <FormKit type="group" name="personalInfo">
          <h4>
            {{ t('tocco.registration.module.personalInfo.title') }}
          </h4>

          <div class="form-section">
            <PersonalInfo />
            <Phones />
            <ProfessionalInfo
              :position-label="t('tocco.registration.module.personalInfo.position')"
              :profession-label="t('tocco.registration.module.personalInfo.profession')"
            />

            <Date
              id="module-registration-birthdate"
              name="birthdate"
              :label="t('tocco.registration.personalDetails.birthdate')"
              :required="true"
              :before="new Date()"
            />

            <p v-if="showFphMemberNumber" class="help-text">
              {{ t('tocco.registration.module.personalInfo.fphMemberHelpText') }}
            </p>
            <Input
              v-if="showFphMemberNumber"
              id="module-registration-fph-member-number"
              name="fphMemberNumber"
              :required="false"
              :label="t('tocco.registration.module.personalInfo.fphMemberNumber')"
            />
          </div>
        </FormKit>

        <div class="separator" />

        <FormKit type="group" name="privateAddress">
          <h4>
            {{ t('tocco.registration.module.privateAddress.title') }}
          </h4>
          <div class="form-section">
            <Address :address-required="addressRequired" />
          </div>
        </FormKit>

        <div class="separator" />

        <FormKit type="group" name="company">
          <h4>{{ t('tocco.registration.module.company.title') }}</h4>
          <div class="form-section">
            <Company v-if="showCompanyAddress" />

            <div class="hide-company-checkbox">
              <Checkbox
                id="module-registration-hide-company"
                v-model="hideCompany"
                name="hideCompany"
                :label="t('tocco.registration.module.company.hideCompany')"
                :required="false"
              />
            </div>
          </div>
        </FormKit>

        <div class="separator" />

        <FormKit type="group" name="invoiceAddress">
          <h4>
            {{ t('tocco.registration.module.invoiceAddress.title') }}
          </h4>

          <div class="form-section">
            <Radio
              id="module-registration-invoice-address-active"
              name="invoiceAddressActive"
              :options="[
                { label: t('tocco.registration.module.invoiceAddress.privateAddress'), value: 'privateAddress' },
                {
                  label: t('tocco.registration.module.invoiceAddress.company'),
                  value: 'company',
                  attrs: { disabled: !showCompanyAddress },
                },
                {
                  label: t('tocco.registration.module.invoiceAddress.invoiceAddress'),
                  value: 'invoiceAddress',
                  attrs: { disabled: !showCompanyAddress },
                },
              ]"
              :required="true"
              @input="(value: string) => (showInvoiceAddress = value === 'invoiceAddress')"
            />

            <Company v-if="showInvoiceAddress" :has-department-billing-information="true" />
          </div>
        </FormKit>

        <div class="separator" />

        <h4>
          {{ t('tocco.registration.module.message.title') }}
        </h4>

        <div class="form-section">
          <Textarea
            id="module-registration-message"
            name="message"
            :label="t('tocco.registration.module.message.label')"
            :required="false"
          />
        </div>

        <div class="buttons-wrapper">
          <FormKitMessages />
          <Button primary slide class="submit-button">
            {{ t('tocco.registration.module.submit') }}
          </Button>
        </div>
      </FormKit>

      <ToccoRegistrationModuleReview
        v-if="showReview && formData && computedData"
        :form-data="formData"
        :address-required="addressRequired"
        :entity="computedData"
        @cancel="onCancel"
      />
    </div>
</FormKitLazyProvider>

  </Section>
</template>
<style lang="scss" scoped>
@use '@/abstract/mixins' as *;

.form-title {
  margin-bottom: 75px;

  @include breakpoint(tl) {
    margin-bottom: 95px;
  }

  h6 {
    margin-bottom: 9px;
  }

  h1 {
    margin-bottom: 0;
  }

  .asterisk {
    font-size: 14px;
  }
}
.hide-company-checkbox {
  margin-top: calc(32px - var(--form-element-gap));
}

.help-text {
  margin-top: var(--form-element-gap);
  margin-bottom: 0;
  font-size: 16px;
}

.buttons-wrapper {
  margin-top: 32px;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 30px;

  @include breakpoint(tl) {
    align-items: center;
    flex-direction: row;
  }

  .submit-button {
    margin-left: auto;
  }
}
</style>
